import React, { useState, useEffect, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { Element as ElementType, MachineType, MachineState, DeconstructState } from '../types';

import { Machine, ElementsPanel, ThemeToggle } from '../components';
import CustomDragLayer from '../components/CustomDragLayer';
import { combineElements, getRandomWord, deconstructElement } from '../lib/api';
import { useToast } from '@/hooks/use-sonner-toast';
import { Confetti, ConfettiRef } from '../components/ui/confetti';
import { v4 as uuidv4 } from 'uuid';
import { isMobile } from 'react-device-detect';
import { AuthButton } from '@/components/auth/AuthButton';
import GoalPanel from '../components/GoalPanel';
// Removed unused import: import SkipButton from '../components/SkipButton';
import DeconstructMachine from '@/components/DeconstructMachine';
import FloatingKey from '@/components/FloatingKey';
import { StatusProvider, useStatus } from '@/contexts/StatusContext';

import { HelpMenu } from '../components/HelpMenu';
import { InteractionModeProvider } from '../contexts/InteractionModeContext';
import { ReportingModeProvider, useReportingMode } from '../contexts/ReportingModeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useActions } from '@/contexts/ActionContext';
import { useGameState } from '@/contexts/GameStateContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PaymentModal from '@/components/PaymentModal';
import SettingsMenu from '@/components/SettingsMenu';
import EmailVerificationAlert from '@/components/auth/EmailVerificationAlert';
import DangerZoneMenu from '@/components/DangerZoneMenu';
import { SparklesText } from '@/components/ui/sparkles-text';

const ForgePage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, isAuthenticated } = useAuth();

  // Redirect non-logged-in users or non-verified email users to the landing page
  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return; // Add early return
    }

    // Redirect users who aren't authenticated (not verified or not Google auth)
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
  }, [currentUser, isAuthenticated, navigate]);

  // If no user or not authenticated, return null to prevent any rendering
  if (!currentUser || !isAuthenticated) {
    return null;
  }

  const { useGameAction, actionsRemaining, isPremium } = useActions();
  const {
    elements: allElements,
    setElements: setAllElements,
    favorites,
    // Removed unused: setFavorites,
    discoveries,
    setDiscoveries,
    points,
    setPoints,
    skipCount,
    setSkipCount,
    keyCount,
    setKeyCount,
    isLoading: gameStateLoading,
    incrementStat,
    addCombination,
  } = useGameState();

  const { setStatus, clearStatus } = useStatus();

  const { toast } = useToast();
  const confettiRef = useRef<ConfettiRef>(null);

  // Machine states
  const [itemMachineState, setItemMachineState] = useState<MachineState>({
    input1: null,
    input2: null,
    output: null,
    isProcessing: false,
  });

  const [locationMachineState, setLocationMachineState] = useState<MachineState>({
    input1: null,
    input2: null,
    output: null,
    isProcessing: false,
  });

  const [characterMachineState, setCharacterMachineState] = useState<MachineState>({
    input1: null,
    input2: null,
    output: null,
    isProcessing: false,
  });

  // Deconstruct machine state
  const [deconstructMachineState, setDeconstructMachineState] = useState<DeconstructState>({
    input: null,
    outputs: null,
    isProcessing: false,
  });

  // Use the deconstructions from GameState
  const { deconstructions, addDeconstruction, hasDeconstruction } = useGameState();

  // Key collection state
  const [showFloatingKey, setShowFloatingKey] = useState<boolean>(false);

  // Element highlight counts
  const [elementHighlightCounts, setElementHighlightCounts] = useState<Record<string, number>>({});

  // Goal state
  const [droppedGoalElement, setDroppedGoalElement] = useState<ElementType | null>(null);
  const [skipMode, setSkipMode] = useState(false);

  // New discoveries tracking
  const [newDiscoveries, setNewDiscoveries] = useState<Record<string, boolean>>({});

  // Payment modal state
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  // Backend for drag and drop
  const dndBackend = isMobile ? TouchBackend : HTML5Backend;

  // Get reporting mode from context
  const { isReportingMode } = useReportingMode();

  // Helper function to get machine state by type
  const getMachineState = (type: MachineType): MachineState => {
    switch (type) {
      case 'Item':
        return itemMachineState;
      case 'Location':
        return locationMachineState;
      case 'Character':
        return characterMachineState;
    }
  };

  // Helper function to get machine state setter by type
  const getMachineStateSetter = (type: MachineType) => {
    switch (type) {
      case 'Item':
        return setItemMachineState;
      case 'Location':
        return setLocationMachineState;
      case 'Character':
        return setCharacterMachineState;
    }
  };

  // Handle dropping an element to a machine
  const handleDropToMachine = (
    machineType: MachineType,
    slot: 'input1' | 'input2',
    element: ElementType
  ) => {
    // Check if the machine is locked
    if (machineType !== 'Item' && !currentUser) {
      toast({
        title: 'Feature Locked',
        description: 'Sign in to unlock the ' + machineType + ' Forge.',
        variant: 'destructive',
      });
      return;
    }

    if (machineType === 'Character' && !isPremium) {
      toast({
        title: 'Feature Locked',
        description: 'Purchase actions to unlock the Character Forge.',
        variant: 'destructive',
      });
      return;
    }

    const setMachineState = getMachineStateSetter(machineType);

    setMachineState((prev) => ({
      ...prev,
      [slot]: element,
    }));
  };

  // Handle combining elements
  const handleCombineElements = async (machineType: MachineType) => {
    // Check if the machine is locked
    if (machineType !== 'Item' && !currentUser) {
      toast({
        title: 'Feature Locked',
        description: 'Sign in to unlock the ' + machineType + ' Forge.',
        variant: 'destructive',
      });
      return;
    }

    if (machineType === 'Character' && !isPremium) {
      toast({
        title: 'Feature Locked',
        description: 'Purchase actions to unlock the Character Forge.',
        variant: 'destructive',
      });
      return;
    }

    // Random chance to spawn a key (5% chance)
    if (isPremium && Math.random() < 0.05 && !showFloatingKey) {
      setShowFloatingKey(true);
    }

    const machineState = getMachineState(machineType);
    const setMachineState = getMachineStateSetter(machineType);

    if (!machineState.input1 || !machineState.input2) return;

    // Use an action
    const actionResult = await useGameAction('combine', machineType);

    if (!actionResult.success) {
      // No payment functionality
      return;
    }

    try {
      setMachineState((prev) => ({
        ...prev,
        isProcessing: true,
        output: null,
      }));

      const result = await combineElements(
        machineType,
        machineState.input1.name,
        machineState.input2.name
      );

      // Track this combination with forge type
      addCombination(machineState.input1.name, machineState.input2.name, result.name, machineType);

      // Track word usage for analytics
      if (currentUser) {
        try {
          // Import the new trackWordUsage function
          const { trackWordUsage } = await import('@/lib/firebase/wordUsageTracking');

          // Track input words with relationship to output

          const input1Result = await trackWordUsage(
            machineState.input1.name,
            true, // isInput = true
            machineType,
            currentUser.uid,
            [result.name] // Related output words
          );

          const input2Result = await trackWordUsage(
            machineState.input2.name,
            true, // isInput = true
            machineType,
            currentUser.uid,
            [result.name] // Related output words
          );

          // Track output word with relationship to inputs

          const outputResult = await trackWordUsage(
            result.name,
            false, // isInput = false
            machineType,
            currentUser.uid,
            [machineState.input1.name, machineState.input2.name] // Related input words
          );
        } catch (trackingError) {
          if (trackingError instanceof Error) {
          }
          // Don't block the main flow if tracking fails
        }
      } else {
      }

      // Check if this element already exists
      const existingElement = allElements.find(
        (e) => e.name.toLowerCase() === result.name.toLowerCase()
      );

      if (existingElement) {
        // Use existing element and clear inputs
        setMachineState((prev) => ({
          ...prev,
          input1: null,
          input2: null,
          isProcessing: false,
          output: existingElement,
        }));

        // Update highlight counts
        setElementHighlightCounts((prev) => ({
          ...prev,
          [existingElement.id]: (prev[existingElement.id] || 0) + 1,
        }));

        // Remove from newDiscoveries if it was previously marked as new
        if (newDiscoveries[existingElement.id]) {
          setNewDiscoveries((prev) => {
            const updated = { ...prev };
            delete updated[existingElement.id];
            return updated;
          });
        }

        // Show toast for existing element
        toast({
          title: 'Element Combined',
          description: `You created ${existingElement.name} again!`,
        });

        // Increment forge stats even for existing elements
        incrementStat('totalForged');

        // Increment the specific forge type stat
        if (machineType === 'Item') {
          incrementStat('itemsForged');
        } else if (machineType === 'Location') {
          incrementStat('locationsForged');
        } else if (machineType === 'Character') {
          incrementStat('charactersForged');
        }
      } else {
        // Create new element with forgeType and description
        const newElement: ElementType = {
          id: uuidv4(),
          name: result.name,
          type: 'discovery',
          color: result.color,
          forgeType: machineType, // Store which forge created this element
          description: result.description, // Add the description
        };

        // Add to all elements
        setAllElements([...allElements, newElement]);

        // Add to discoveries
        setDiscoveries([...discoveries, newElement]);

        // Increment stats
        incrementStat('totalDiscoveries');
        incrementStat('totalForged');

        // Increment the specific forge type stat
        if (machineType === 'Item') {
          incrementStat('itemsForged');
        } else if (machineType === 'Location') {
          incrementStat('locationsForged');
        } else if (machineType === 'Character') {
          incrementStat('charactersForged');
        }

        // Set as output and clear inputs
        setMachineState((prev) => ({
          ...prev,
          input1: null,
          input2: null,
          isProcessing: false,
          output: newElement,
        }));

        // Mark as new discovery
        setNewDiscoveries((prev) => ({
          ...prev,
          [newElement.id]: true,
        }));

        // Show confetti for new discovery with multiple bursts
        if (confettiRef.current) {
          // Create a confetti animation similar to the one in Index.tsx
          const duration = 2 * 1000;
          const animationEnd = Date.now() + duration;
          const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

          const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

          const interval = setInterval(() => {
            const timeLeft = animationEnd - Date.now();

            if (timeLeft <= 0) {
              return clearInterval(interval);
            }

            const particleCount = 50 * (timeLeft / duration);

            // Fire multiple confetti bursts from different positions
            confettiRef.current?.fire({
              ...defaults,
              particleCount,
              origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
            });

            confettiRef.current?.fire({
              ...defaults,
              particleCount,
              origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
            });

            confettiRef.current?.fire({
              ...defaults,
              particleCount,
              origin: { x: randomInRange(0.4, 0.6), y: Math.random() - 0.2 },
            });
          }, 200);
        }

        // Show toast for new discovery
        toast({
          title: 'New Discovery!',
          description: `You discovered ${newElement.name}!`,
          variant: 'default',
        });
      }
    } catch (error) {
      // Return to input state without clearing inputs
      setMachineState((prev) => ({
        ...prev,
        isProcessing: false,
        output: null,
      }));

      // Show a toast with a more user-friendly message
      toast({
        title: 'Forge Jammed',
        description: 'The forge is jammed. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Handle clearing a machine
  const handleClearMachine = (machineType: MachineType) => {
    const setMachineState = getMachineStateSetter(machineType);

    setMachineState({
      input1: null,
      input2: null,
      output: null,
      isProcessing: false,
    });
  };

  // Handle output drag end
  const handleOutputDragEnd = (machineType: MachineType) => {
    const machineState = getMachineState(machineType);
    const setMachineState = getMachineStateSetter(machineType);

    if (machineState.output) {
      // Clear the output
      setMachineState((prev) => ({
        ...prev,
        output: null,
      }));
    }
  };

  // Placeholder for handleAddToFavorites - keeping for compatibility
  const handleAddToFavorites = (_element: ElementType) => {
    // Favorites feature has been removed
  };

  // Handle dropping an element to a goal
  const handleDropToGoal = (element: ElementType) => {
    setDroppedGoalElement(element);
  };

  // Handle goal submission
  const handleGoalSubmit = () => {
    // Clear the dropped element
    setDroppedGoalElement(null);
  };

  // Handle points earned
  const handlePointsEarned = (pointsEarned: number) => {
    setPoints(points + pointsEarned);

    // Add 3 skips for each goal completion
    if (pointsEarned === 50) {
      // This is from a goal completion (we set it to 50 points)
      setSkipCount(skipCount + 3);
      // Increment the skipsEarned stat
      incrementStat('skipsEarned', 3);

      toast({
        title: 'Skips Earned!',
        description: 'You earned 3 skips for completing a goal!',
        variant: 'warning',
      });
    }
    // Also keep the original skip for every 50 points logic
    else if (Math.floor((points + pointsEarned) / 50) > Math.floor(points / 50)) {
      setSkipCount(skipCount + 1);
      // Increment the skipsEarned stat
      incrementStat('skipsEarned', 1);

      toast({
        title: 'Skip Earned!',
        description:
          'You earned a skip for reaching ' +
          Math.floor((points + pointsEarned) / 50) * 50 +
          ' points!',
        variant: 'warning',
      });
    }
  };

  // These functions are now handled directly in the GoalPanel component
  // Keeping the functions but marking them as unused with underscore prefix
  // Handle skip activation
  const _handleSkipActivated = () => {
    if (skipCount <= 0) {
      toast({
        title: 'No Skips Available',
        description: 'Complete goals to earn skips.',
        variant: 'destructive',
      });
      return;
    }

    setSkipMode(true);
    // Don't decrement skip count here, only when a skip is actually used

    toast({
      title: 'Skip Activated',
      description: 'Click on a goal to skip it.',
      variant: 'warning',
    });

    // Increment the skipsEarned stat if not already tracked
    incrementStat('skipsEarned', 0); // Just ensure the stat exists
  };

  // Handle skip cancellation
  const _handleSkipCancelled = () => {
    setSkipMode(false);
  };

  // Handle skip used
  const handleSkipUsed = async (
    goalType: 'Item' | 'Location' | 'Character',
    currentWord?: string,
    recentWords: string[] = []
  ) => {
    // Reset the skip mode
    setSkipMode(false);

    // Decrement skip count when a skip is actually used
    setSkipCount(skipCount - 1);

    // Increment the skipsUsed stat
    incrementStat('skipsUsed');

    try {
      // Get a new random word for the skipped goal, passing the current word and recent words to ensure we get a different one

      const newWord = await getRandomWord(goalType, currentWord, recentWords);

      return newWord;
    } catch (error) {
      // Return default values if API fails
      return goalType === 'Item' ? 'Shield' : goalType === 'Location' ? 'Paris' : 'Cleopatra';
    }
  };

  // Handle key collection
  const handleKeyCollected = () => {
    setShowFloatingKey(false);
    setKeyCount(keyCount + 1);
    // Increment the keysCollected stat
    incrementStat('keysCollected');
  };

  // Handle dropping an element to the deconstruct machine
  const handleDropToDeconstruct = (element: ElementType) => {
    setDeconstructMachineState((prev) => ({
      ...prev,
      input: element,
      outputs: null,
    }));
  };

  // Handle deconstructing an element
  const handleDeconstructElement = async () => {
    if (!deconstructMachineState.input || keyCount <= 0) return;

    // Add this element to the deconstruction history
    if (deconstructMachineState.input) {
      addDeconstruction(deconstructMachineState.input.name);
    }

    try {
      // Set processing state
      setDeconstructMachineState((prev) => ({
        ...prev,
        isProcessing: true,
      }));

      // Update status with thematic messages
      setStatus('Analyzing structure...');

      // Short delay for visual effect
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Use a key
      setKeyCount(keyCount - 1);
      incrementStat('keysUsed');
      incrementStat('deconstructions');

      // Update status
      setStatus('Deconstructing element...');

      // Get the forge type of the element or default to "Item"
      const forgeType = deconstructMachineState.input.forgeType || 'Item';

      // Call the API to deconstruct the element
      const result = await deconstructElement(deconstructMachineState.input.name, forgeType);

      // Note: Word usage tracking moved to handleSelectDeconstructOutput
      // so we only track the word that the user actually chooses

      // Update status
      setStatus('Extracting components...');

      // Short delay for visual effect
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Create element objects from the components
      const outputElements: ElementType[] = [];

      for (const component of result.components) {
        // Check if this element already exists
        const existingElement = allElements.find(
          (e) => e.name.toLowerCase() === component.name.toLowerCase()
        );

        if (existingElement) {
          // Use existing element
          outputElements.push(existingElement);

          // Make sure it's not marked as a new discovery
          if (newDiscoveries[existingElement.id]) {
            setNewDiscoveries((prev) => {
              const updated = { ...prev };
              delete updated[existingElement.id];
              return updated;
            });
          }
        } else {
          // Create new element
          const newElement: ElementType = {
            id: uuidv4(),
            name: component.name,
            type: 'discovery',
            color: component.color,
            forgeType: forgeType as 'Item' | 'Location' | 'Character',
            description: component.description, // Add the description
          };

          outputElements.push(newElement);

          // Mark as new discovery
          setNewDiscoveries((prev) => ({
            ...prev,
            [newElement.id]: true,
          }));
        }
      }

      // If we don't have exactly 2 components, add fallbacks
      if (outputElements.length < 2) {
        const inputName = deconstructMachineState.input.name.toLowerCase();
        const basicElements = [
          { name: 'Water', color: '#3b82f6', description: 'A clear liquid essential for life.' },
          {
            name: 'Fire',
            color: '#ef4444',
            description: 'A chemical reaction that produces heat and light.',
          },
          { name: 'Earth', color: '#84cc16', description: 'The soil and ground beneath our feet.' },
          {
            name: 'Air',
            color: '#a1a1aa',
            description: 'The invisible mixture of gases surrounding our planet.',
          },
        ];

        let fallbacksAdded = 0;

        for (const element of basicElements) {
          if (
            element.name.toLowerCase() !== inputName &&
            fallbacksAdded < 2 - outputElements.length
          ) {
            const newElement: ElementType = {
              id: uuidv4(),
              name: element.name,
              type: 'discovery',
              color: element.color,
              forgeType: forgeType as 'Item' | 'Location' | 'Character',
              description: element.description, // Add the description
            };

            outputElements.push(newElement);
            fallbacksAdded++;
          }
        }
      }

      // Update deconstruct machine state - keep input until user selects output
      setDeconstructMachineState({
        input: deconstructMachineState.input, // Keep the input for tracking
        outputs: outputElements,
        isProcessing: false,
      });

      // Clear status when done
      clearStatus();

      // Note: We already incremented keysUsed when we used the key earlier
      // No need to increment it again here

      // Show success toast
      toast({
        title: 'Element Deconstructed!',
        description: `You broke down ${deconstructMachineState.input.name} into its components.`,
        variant: 'default',
      });
    } catch (error) {
      // Return to input state without clearing the input
      setDeconstructMachineState((prev) => ({
        ...prev,
        isProcessing: false,
        outputs: null,
      }));

      // Clear status on error
      clearStatus();

      // Show a toast with a more user-friendly message
      toast({
        title: 'Deconstructor Jammed',
        description: 'The deconstructor is jammed. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Handle clearing the deconstruct machine
  const handleClearDeconstruct = () => {
    setDeconstructMachineState({
      input: null,
      outputs: null,
      isProcessing: false,
    });
  };

  // Handle selecting an output from the deconstruct machine
  const handleSelectDeconstructOutput = async (element: ElementType) => {
    // Track word usage for analytics - only track the word that was actually chosen
    if (currentUser && deconstructMachineState.input) {
      try {
        // Use direct Firestore operations to ensure it works
        const { doc, setDoc, getDoc, updateDoc, increment, arrayUnion, serverTimestamp } =
          await import('firebase/firestore');
        const { db } = await import('@/lib/firebase/firestore');

        const normalizedWord = element.name.toLowerCase().trim();
        const wordRef = doc(db, 'word_usage', normalizedWord);

        // Check if document exists
        const wordDoc = await getDoc(wordRef);

        if (wordDoc.exists()) {
          // Update existing document
          await updateDoc(wordRef, {
            count: increment(1),
            outputCount: increment(1),
            deconstructorOutputCount: increment(1),
            timestamp: serverTimestamp(),
            users: arrayUnion(currentUser.uid),
          });
        } else {
          // Create new document
          await setDoc(wordRef, {
            word: normalizedWord,
            count: 1,
            inputCount: 0,
            outputCount: 1,
            itemForgeCount: 0,
            locationForgeCount: 0,
            characterForgeCount: 0,
            deconstructorOutputCount: 1,
            timestamp: serverTimestamp(),
            users: [currentUser.uid],
            inputToOutputs: {},
            outputFromInputs: {
              [deconstructMachineState.input.name.toLowerCase().trim()]: {
                count: 1,
                forgeTypes: {
                  Deconstructor: 1,
                },
              },
            },
          });
        }
      } catch (trackingError) {
        // Don't block the main flow if tracking fails
      }
    }

    // Check if this element already exists in allElements
    const existingElement = allElements.find((e) => e.id === element.id);

    // Check if this element already exists by name (different ID)
    const existingElementByName = allElements.find(
      (e) => e.name.toLowerCase() === element.name.toLowerCase() && e.id !== element.id
    );

    if (!existingElement && !existingElementByName) {
      // This is a truly new element
      // Add the new element to allElements
      setAllElements([...allElements, element]);

      // Add to discoveries
      setDiscoveries([...discoveries, element]);

      // Increment stats
      incrementStat('totalDiscoveries');
    } else if (existingElementByName) {
      // If we found an existing element with the same name but different ID,
      // we should use that element instead and make sure it's not marked as new
      if (newDiscoveries[element.id]) {
        setNewDiscoveries((prev) => {
          const updated = { ...prev };
          delete updated[element.id];
          return updated;
        });
      }
    }

    // Add the selected element to highlights
    setElementHighlightCounts((prev) => ({
      ...prev,
      [element.id]: (prev[element.id] || 0) + 1,
    }));

    // Clear the deconstruct machine after tracking
    setDeconstructMachineState({
      input: null,
      outputs: null,
      isProcessing: false,
    });

    // Get the position of the Deconstructor
    const deconstructorElement = document.querySelector('.deconstructor-machine');
    let origin = { x: 0.5, y: 0.5 }; // Default to center if we can't find the element

    if (deconstructorElement) {
      const rect = deconstructorElement.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      // Convert to origin coordinates (0-1 range)
      origin = {
        x: centerX / window.innerWidth,
        y: centerY / window.innerHeight,
      };
    }

    // Fire one large burst of confetti over the Deconstructor
    confettiRef.current!.fire({
      particleCount: 150,
      spread: 100,
      origin: origin,
      colors: ['#FFD700', '#FFC800', '#FFBF00', '#FFD700', '#FFDF00'],
      shapes: ['circle', 'square'],
      scalar: 1.5, // Larger particles
      gravity: 0.8,
      ticks: 200,
    });
  };

  // If game state is loading, show a loading screen
  if (gameStateLoading) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-background z-50">
        <div className="flex items-center justify-center gap-4 mb-4">
          <img
            src="/images/elements.png"
            alt="Elements"
            className="w-16 h-16 object-contain drop-shadow-[2px_2px_3px_rgba(0,0,0,0.3)] animate-pulse"
          />
          <SparklesText
            text="VibeCraft"
            className="text-4xl md:text-5xl font-bold text-foreground"
            colors={{
              first: '#7C3AED', // Darker purple (violet-600)
              second: '#2563EB', // Darker blue (blue-600)
            }}
            sparklesCount={12}
          />
        </div>
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-lg text-muted-foreground">Loading your elements...</p>
        </div>
      </div>
    );
  }

  return (
    <ReportingModeProvider>
      <InteractionModeProvider>
        <DndProvider backend={dndBackend}>
          <CustomDragLayer />
          <Confetti
            ref={confettiRef}
            className="fixed left-0 top-0 z-50 w-full h-full pointer-events-none"
          />
          <div
            className={`w-full px-4 py-8 overflow-visible ${
              isReportingMode ? 'blur-sm pointer-events-none' : ''
            }`}
          >
            {/* Mobile-friendly header with responsive layout */}
            <header className="mb-8 relative w-full">
              {/* Desktop header - hidden on small screens */}
              <div className="hidden md:flex items-center justify-between w-full">
                {/* Left side: Back button, Score and Skips */}
                <div className="flex items-center gap-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/')}
                    className="flex items-center gap-1"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </Button>

                  {/* Score, Skips, and Keys */}
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-bold">Score:</span>
                      <span className="text-sm font-bold text-primary">{points}</span>
                    </div>

                    <div className="flex items-center gap-1">
                      <span className="text-xs font-bold">Skips:</span>
                      <span className="text-sm font-bold text-amber-500">{skipCount}</span>
                    </div>

                    <div className="flex items-center gap-1">
                      <span className="text-xs font-bold">Keys:</span>
                      <span className="text-sm font-bold text-yellow-500">{keyCount}</span>
                    </div>
                  </div>
                </div>

                {/* Center: VibeCraft title */}
                <div className="flex items-center justify-center gap-2 absolute left-1/2 transform -translate-x-1/2">
                  <img
                    src="/images/elements.png"
                    alt="Elements"
                    className="w-10 h-10 object-contain drop-shadow-[2px_2px_3px_rgba(0,0,0,0.3)]"
                  />
                  <SparklesText
                    text="VibeCraft"
                    className="text-2xl md:text-3xl font-bold text-foreground"
                    colors={{
                      first: '#7C3AED', // Darker purple (violet-600)
                      second: '#2563EB', // Darker blue (blue-600)
                    }}
                    sparklesCount={8}
                  />
                </div>

                {/* Right side: Actions and menus */}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setIsPaymentModalOpen(true)}
                  >
                    Get Actions
                  </Button>
                  <div className="flex items-center gap-1 text-sm bg-card px-3 py-1 rounded-full border border-border">
                    <span className="font-medium">Actions:</span>
                    <span
                      className={actionsRemaining <= 3 ? 'text-red-500 font-bold' : 'font-bold'}
                    >
                      {actionsRemaining}
                    </span>
                  </div>
                  <DangerZoneMenu />
                  <HelpMenu />
                  <SettingsMenu />
                  <AuthButton />
                  <ThemeToggle />
                </div>
              </div>

              {/* Mobile header - shown only on small screens */}
              <div className="md:hidden flex flex-col w-full">
                {/* Top row: Back button, VibeCraft title, and menu buttons */}
                <div className="flex items-center justify-between w-full mb-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/')}
                    className="flex items-center gap-1"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    <span className="sr-only">Back</span>
                  </Button>

                  {/* Center: VibeCraft title */}
                  <div className="flex items-center justify-center gap-2">
                    <img
                      src="/images/elements.png"
                      alt="Elements"
                      className="w-8 h-8 object-contain drop-shadow-[2px_2px_3px_rgba(0,0,0,0.3)]"
                    />
                    <SparklesText
                      text="VibeCraft"
                      className="text-xl font-bold text-foreground"
                      colors={{
                        first: '#7C3AED', // Darker purple (violet-600)
                        second: '#2563EB', // Darker blue (blue-600)
                      }}
                      sparklesCount={6}
                    />
                  </div>

                  {/* Essential buttons */}
                  <div className="flex items-center gap-1">
                    <DangerZoneMenu />
                    <HelpMenu />
                    <SettingsMenu />
                    <AuthButton />
                    <ThemeToggle />
                  </div>
                </div>

                {/* Second row: Stats and Actions */}
                <div className="flex items-center justify-between w-full">
                  {/* Score, Skips, and Keys */}
                  <div className="flex items-center gap-2 text-xs">
                    <div className="flex items-center gap-1 bg-card px-2 py-1 rounded-full border border-border">
                      <span className="font-medium">Score:</span>
                      <span className="font-bold text-primary">{points}</span>
                    </div>

                    <div className="flex items-center gap-1 bg-card px-2 py-1 rounded-full border border-border">
                      <span className="font-medium">Skips:</span>
                      <span className="font-bold text-amber-500">{skipCount}</span>
                    </div>

                    <div className="flex items-center gap-1 bg-card px-2 py-1 rounded-full border border-border">
                      <span className="font-medium">Keys:</span>
                      <span className="font-bold text-yellow-500">{keyCount}</span>
                    </div>
                  </div>

                  {/* Actions and Get Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setIsPaymentModalOpen(true)}
                    >
                      Get Actions
                    </Button>
                    <div className="flex items-center gap-1 text-xs bg-card px-2 py-1 rounded-full border border-border">
                      <span className="font-medium">Actions:</span>
                      <span
                        className={actionsRemaining <= 3 ? 'text-red-500 font-bold' : 'font-bold'}
                      >
                        {actionsRemaining}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </header>

            {/* Goals Panel */}
            <div className="mb-4">
              <GoalPanel
                droppedElement={droppedGoalElement}
                skipMode={skipMode}
                onSubmit={handleGoalSubmit}
                onPointsEarned={handlePointsEarned}
                onSkipUsed={handleSkipUsed}
              />
            </div>

            <PaymentModal
              isOpen={isPaymentModalOpen}
              onClose={() => setIsPaymentModalOpen(false)}
            />

            {/* Main content: Elements & Discoveries (75%) and Forges (25%) */}
            <div className="flex flex-col md:flex-row gap-4 overflow-visible">
              {/* Left side: Elements & Discoveries panel (75%) */}
              <div className="w-full md:w-3/4">
                <ElementsPanel
                  elements={allElements}
                  highlightCounts={elementHighlightCounts}
                  onAddToFavorites={handleAddToFavorites}
                  onElementDragEnd={handleDropToGoal}
                  title="Elements & Discoveries"
                  description="Drag elements to forges to combine them"
                  favorites={favorites}
                />
              </div>

              {/* Right side: Vertically stacked Forges (25%) */}
              <div className="w-full md:w-1/4">
                <div className="flex flex-col gap-2">
                  {/* Item Forge */}
                  <Machine
                    type="Item"
                    state={itemMachineState}
                    onDrop={(slot, element) => handleDropToMachine('Item', slot, element)}
                    onCombine={() => handleCombineElements('Item')}
                    onClear={() => handleClearMachine('Item')}
                    onOutputDragEnd={() => handleOutputDragEnd('Item')}
                    newDiscoveries={newDiscoveries}
                  />

                  {/* Location Forge */}
                  <Machine
                    type="Location"
                    state={locationMachineState}
                    onDrop={(slot, element) => handleDropToMachine('Location', slot, element)}
                    onCombine={() => handleCombineElements('Location')}
                    onClear={() => handleClearMachine('Location')}
                    onOutputDragEnd={() => handleOutputDragEnd('Location')}
                    newDiscoveries={newDiscoveries}
                    locked={!currentUser}
                  />

                  {/* Character Forge */}
                  <Machine
                    type="Character"
                    state={characterMachineState}
                    onDrop={(slot, element) => handleDropToMachine('Character', slot, element)}
                    onCombine={() => handleCombineElements('Character')}
                    onClear={() => handleClearMachine('Character')}
                    onOutputDragEnd={() => handleOutputDragEnd('Character')}
                    newDiscoveries={newDiscoveries}
                    locked={!isPremium}
                  />

                  {/* Deconstructor */}
                  <DeconstructMachine
                    state={deconstructMachineState}
                    onDrop={handleDropToDeconstruct}
                    onDeconstruct={handleDeconstructElement}
                    onClear={handleClearDeconstruct}
                    newDiscoveries={newDiscoveries}
                    onSelectOutput={handleSelectDeconstructOutput}
                    isDeconstructionTried={
                      deconstructMachineState.input
                        ? hasDeconstruction(deconstructMachineState.input.name)
                        : false
                    }
                  />
                </div>
              </div>
            </div>

            {/* Floating Key */}
            {showFloatingKey && <FloatingKey onCollect={handleKeyCollected} />}
          </div>
        </DndProvider>
      </InteractionModeProvider>
    </ReportingModeProvider>
  );
};

// Wrap the ForgePage with StatusProvider
const ForgePageWithStatus: React.FC = () => {
  return (
    <StatusProvider>
      <ForgePage />
    </StatusProvider>
  );
};

export default ForgePageWithStatus;
